import { SearchAdapter, SearchRequest, SearchResponse } from '@empathy/search-adapter';
import { NextQuery } from '@empathy/search-types';
import { container } from '../../../container';
import { DEPENDENCIES } from '../../../container/dependencies.const';
import { EmpathyXConfig, Facets } from '../../../models';
import { catchAdapterError, mapWishlistToResults } from '../../../utils';
import { GETTERS_KEYS } from '../../store.const';
import { QUERY_SIGNALS_ACTIONS_KEYS, QUERY_SIGNALS_GETTERS_KEYS, QUERY_SIGNALS_MUTATIONS_KEYS } from '../query-signals.const';
import { QuerySignalsAction, QuerySignalsContext } from '../query-signals.interfaces';

export class GetNextQueriesResultsAction implements QuerySignalsAction {
  protected context: QuerySignalsContext;
  protected loading = false;

  actions = {
    [QUERY_SIGNALS_ACTIONS_KEYS.getNextQueriesResults]: (context: QuerySignalsContext) => {
      this.context = context;
      this.getNextQueriesResults();
    }
  };

  private getNextQueriesResults() {
    const nextQuery = this.getNextQueryToLoad();
    if (nextQuery) {
      if (this.shouldLoadMoreNextQueryResults && !this.loading) {
        this.loading = true;
        this.getNextQueryResults(nextQuery)
        // this finally must be before the `then` because it's necessary to reset the `loading` flag before the next `getNextQueriesResults`
          .finally(() => this.loading = false)
          .then((searchResponse: SearchResponse) => {
            const newFields = this.getNewFields(searchResponse);
            this.context.commit(QUERY_SIGNALS_MUTATIONS_KEYS.updateNextQuery, { nextQuery, newFields });
            this.context.commit(QUERY_SIGNALS_MUTATIONS_KEYS.setLastNextQueryLoaded, nextQuery);
            this.getNextQueriesResults();
          })
          .catch(catchAdapterError('Next queries results'));
      }
    }
  }

  private get shouldLoadMoreNextQueryResults(): boolean {
    const {
      nextQueries: {
        max: maxNextQueries,
        maxWhenNotReachingTrigger
      }
    }: EmpathyXConfig = this.context.rootGetters[GETTERS_KEYS.config.config];

    const fallbackNextQueriesCondition: boolean =
      !!this.context.rootState.search.query &&
      !this.context.rootGetters[GETTERS_KEYS.search.hasMoreResults] &&
      this.numberOfNextQueriesWithResults < maxWhenNotReachingTrigger;

    const nextQueriesPerPageCondition: boolean =
      this.numberOfNextQueriesWithResults < this.nextQueriesWithResultsShouldBe &&
      this.numberOfNextQueriesWithResults < maxNextQueries;

    return fallbackNextQueriesCondition || nextQueriesPerPageCondition;
  }

  private get nextQueriesWithResultsShouldBe(): number {
    const { nextQueries: { pageFrequency, perLoad, max } }: EmpathyXConfig = this.context.rootGetters[GETTERS_KEYS.config.config];
    const currentPage: number = this.context.rootState.search.page;
    return Math.min(Math.trunc(currentPage / pageFrequency) * perLoad, max);
  }

  private get numberOfNextQueriesWithResults(): number {
    const { length: nextQueriesWithResults }: NextQuery[] = this.context.getters[QUERY_SIGNALS_GETTERS_KEYS.nextQueriesWithResults];
    return nextQueriesWithResults;
  }

  private getNextQueryToLoad(): NextQuery {
    const nextQueries: NextQuery[] = this.context.getters[QUERY_SIGNALS_GETTERS_KEYS.nextQueries];
    const indexOfNextQueryToLoad = nextQueries.indexOf(this.context.state.lastNextQueryLoaded) + 1;
    return nextQueries[indexOfNextQueryToLoad];
  }

  private getNextQueryResults(nextQuery: NextQuery): Promise<SearchResponse> {
    const request = this.getRequest(nextQuery);
    const platformAdapter = container.get<SearchAdapter>(DEPENDENCIES.Adapter);
    return platformAdapter.search(request, { requestId: `next-query-${ nextQuery.query }` });
  }

  private getRequest(nextQuery: NextQuery): SearchRequest {
    const { rootGetters } = this.context;
    const searchRequestNqResults: SearchRequest = rootGetters[GETTERS_KEYS.search.searchRequestNextQueryResults];
    searchRequestNqResults.query = nextQuery.query;
    return searchRequestNqResults;
  }

  private getNewFields(searchResponse: SearchResponse): Partial<NextQuery> {
    const { wishlist } = this.context.rootState.core;
    const facets = this.amendFacets(searchResponse);
    mapWishlistToResults(wishlist, searchResponse.results);
    return {
      results: searchResponse.results,
      resultsFacets: facets,
      numFound: searchResponse.totalResults
    };
  }

  private amendFacets(searchResponse: SearchResponse): Facets {
    const { nextQueries: { facetsToApply } }: EmpathyXConfig = this.context.rootGetters[GETTERS_KEYS.config.config];
    return searchResponse.facets.reduce((facets, facet) => {
      if (facetsToApply.includes(facet.modelName)) {
        facet.filters.forEach(filter => filter.children = []);
        facets[facet.modelName] = facet;
      }
      return facets;
    }, {} as Facets);
  }
}
